import React, { useCallback, useState } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../common/types';
import { Inspector } from './inspector/Inspector';
import { useMusicVideoEditor, type MusicVideoTool, type RecutTarget } from './MusicVideoEditorContext';
import { SidePanel } from './panels/SidePanel';
import { ToolRail } from './panels/ToolRail';
import { PreviewStage } from './preview/PreviewStage';
import { Timeline } from './timeline/Timeline';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the editor; each part uses its own slice of it */
import styles from './MusicVideoEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WideEditorProps {
  topBar: React.ReactNode;
  notices: React.ReactNode;
  recutTarget: RecutTarget | null;
  onCloseRecut: () => void;
}

/**
 * The wide layout (design b1): the top bar; the tool rail with its panel, the live preview with its
 * transport, the inspector; the timeline at the bottom. A second click on the open tool closes its
 * panel, so the preview gets the room.
 */
const WideEditorComponent: React.FC<WideEditorProps> = ({ topBar, notices, recutTarget, onCloseRecut }) => {
  const { tool, setTool } = useMusicVideoEditor();
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const pick = useCallback(
    (next: MusicVideoTool) => {
      setIsPanelOpen(next !== tool || !isPanelOpen);
      setTool(next);
    },
    [isPanelOpen, setTool, tool]
  );
  return (
    <div className={styles.editor}>
      {topBar}
      <div className={clsx(styles.body, !isPanelOpen && styles.bodyNoPane)}>
        <ToolRail isPanelOpen={isPanelOpen} onPick={pick} />
        {isPanelOpen && <SidePanel />}
        <div className={styles.stageColumn}>
          {notices}
          <PreviewStage />
        </div>
        <Inspector />
      </div>
      <Timeline recutTarget={recutTarget} onCloseRecut={onCloseRecut} />
    </div>
  );
};

export const WideEditor = TypedMemo(WideEditorComponent);
